import { useTheme } from 'foundations-theming/utils';
import { useState, useMemo } from 'react';
import { Redirect, Route, Switch, generatePath, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { NavMarker } from 'react-rhumb';
import EnrollmentActionConfigHeader from 'automation-ui-enrollment/components/common/EnrollmentActionConfigHeader';
import EnrollmentSaveButton from 'automation-ui-enrollment/components/common/EnrollmentSaveButton';
import TriggerTypeChangeButton from 'automation-ui-enrollment/components/eventEnrollment/TriggerTypeChangeButton';
import EnrollmentSchedule from 'automation-ui-enrollment/records/EnrollmentSchedule';
import { areFilterBranchesEqual, getEnrollmentList } from 'automation-ui-enrollment/utils/listFilters';
import { getEnrollmentListAssociatedListUpdates } from 'automation-ui-enrollment/utils/updateHelpers';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIButton from 'UIComponents/button/UIButton';
import UIPanelBody from 'UIComponents/panel/UIPanelBody';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { getEnrollmentTrackingDataFromCombinedEnrollment } from 'automation-ui-enrollment/utils/trackingUtils';
import { useTrackEvent } from 'automation-ui-usage-tracking/usageTrackingHooks';
import Routes from 'automation-ui-enrollment/constants/Routes';
import UIAlert from 'UIComponents/alert/UIAlert';
import FormattedMessageWithObjectTypeName from 'automation-ui-i18n/FormattedMessageWithObjectTypeName';
import UIPanelSection from 'UIComponents/panel/UIPanelSection';
import ScheduledEnrollmentTriggersTab from 'automation-ui-enrollment/components/scheduledEnrollment/ScheduledEnrollmentTriggersTab';
import ScheduledEnrollmentReenrollmentTab from 'automation-ui-enrollment/components/scheduledEnrollment/ScheduledEnrollmentReenrollmentTab';
import PortalIdParser from 'PortalIdParser';
import { ONCE } from 'automation-ui-enrollment/constants/scheduledEnrollment/ScheduleCadence';
import { isScheduledForPastDate } from 'automation-ui-enrollment/components/scheduledEnrollment/utils/isScheduledForPastDate';
import { parseLocation } from 'automation-ui-routing/utils';
import { getIsObjectlessFlow } from 'automation-ui-flow-core/flow/flowUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ScheduledEnrollmentContainer = ({
  flow,
  onClose,
  readOnly,
  updateEnrollmentCriteria,
  settingsComponent,
  unenrollmentComponent,
  hasSettingsChanged
}) => {
  var _flow$enrollmentCrite;
  const trellisTheme = useTheme();
  const {
    url: currentUrl
  } = useRouteMatch();
  const history = useHistory();
  const location = useLocation();
  const isObjectlessWorkflow = getIsObjectlessFlow(flow);
  const {
    params: {
      revisionId
    }
  } = useRouteMatch();
  const trackEvent = useTrackEvent();
  const initialEnrollmentSchedule = useMemo(() => {
    var _flow$enrollmentSched;
    const defaultSchedule = EnrollmentSchedule.getDefaultScheduleForPortal(PortalIdParser.get());
    return new EnrollmentSchedule((_flow$enrollmentSched = flow.enrollmentSchedule) !== null && _flow$enrollmentSched !== void 0 ? _flow$enrollmentSched : defaultSchedule.setCadence(ONCE).setScheduleWindow('SPECIFIC_TIME'));
  }, [flow.enrollmentSchedule]);
  const initialAssociatedList = getEnrollmentList(flow);
  const initialEnrollmentFilters = initialAssociatedList && initialAssociatedList.filterBranch;
  const [stagedEnrollmentSchedule, setStagedEnrollmentSchedule] = useState(initialEnrollmentSchedule);
  const initialEnrollmentCriteriaForDataset = ((_flow$enrollmentCrite = flow.enrollmentCriteria) === null || _flow$enrollmentCrite === void 0 ? void 0 : _flow$enrollmentCrite.triggerType) === 'DATASET' ? flow.enrollmentCriteria : undefined;
  const [stagedEnrollmentCriteriaForDataset, setStagedEnrollmentCriteriaForDataset] = useState(initialEnrollmentCriteriaForDataset);
  const hasEnrollmentCriteriaChanged = (stagedEnrollmentCriteriaForDataset === null || stagedEnrollmentCriteriaForDataset === void 0 ? void 0 : stagedEnrollmentCriteriaForDataset.datasetId) !== (initialEnrollmentCriteriaForDataset === null || initialEnrollmentCriteriaForDataset === void 0 ? void 0 : initialEnrollmentCriteriaForDataset.datasetId) || (stagedEnrollmentCriteriaForDataset === null || stagedEnrollmentCriteriaForDataset === void 0 ? void 0 : stagedEnrollmentCriteriaForDataset.subviewId) !== (initialEnrollmentCriteriaForDataset === null || initialEnrollmentCriteriaForDataset === void 0 ? void 0 : initialEnrollmentCriteriaForDataset.subviewId);
  const [stagedFilters, setStagedFilters] = useState(initialEnrollmentFilters);
  const [saveStatus, setSaveStatus] = useState('UNINITIALIZED');
  const hasScheduleChanged = !stagedEnrollmentSchedule.equals(initialEnrollmentSchedule);
  const haveFiltersChanged = !areFilterBranchesEqual(stagedFilters, initialEnrollmentFilters);
  const clearEnrollmentListAndSchedule = callback => {
    // wipe existing enrollment data
    updateEnrollmentCriteria({
      associatedLists: [],
      flowEventFilters: flow.flowEventFilters,
      shouldReenroll: flow.shouldReenroll,
      enrollmentTrigger: 'NEW',
      enrollmentSchedule: null,
      enrollmentCriteria: {
        operation: 'DELETE'
      }
    }, callback, err => {
      reportError(err);
    }).catch(() => {});
  };
  const isDataset = !!parseLocation(location).isDataset || !!stagedEnrollmentCriteriaForDataset;
  const saveButtonCommonProps = {
    allowSave: !readOnly && (!flow.enrollmentSchedule || hasScheduleChanged || haveFiltersChanged || !!hasSettingsChanged || hasEnrollmentCriteriaChanged),
    onSave: () => {
      const filtersTrackingData = stagedFilters ? getEnrollmentTrackingDataFromCombinedEnrollment(stagedFilters, []) : {};
      if (isDataset) {
        trackEvent(Trackables.SAVED_DATASET_TRIGGER, {
          assetType: 'dataset',
          assetId: stagedEnrollmentCriteriaForDataset === null || stagedEnrollmentCriteriaForDataset === void 0 ? void 0 : stagedEnrollmentCriteriaForDataset.datasetId
        });
      }
      trackEvent(Trackables.SAVED_ENROLLMENT, Object.assign({
        shouldReenroll: true,
        isEmpty: !stagedFilters || stagedFilters.filterBranches.length === 0,
        objectTypeId: flow.objectTypeId,
        enrollmentType: 'SCHEDULED',
        frequency: stagedEnrollmentSchedule.getCadence(),
        editType: !(flow !== null && flow !== void 0 && flow.enrollmentSchedule) ? 'ADD' : 'EDIT'
      }, filtersTrackingData));
      setSaveStatus('STARTED');
      updateEnrollmentCriteria({
        shouldReenroll: true,
        associatedLists: getEnrollmentListAssociatedListUpdates(initialAssociatedList, stagedFilters),
        flowEventFilters: flow.flowEventFilters,
        enrollmentTrigger: 'LIST',
        enrollmentSchedule: stagedEnrollmentSchedule.toJS(),
        enrollmentCriteria: stagedEnrollmentCriteriaForDataset ? {
          value: stagedEnrollmentCriteriaForDataset,
          operation: 'UPSERT'
        } : undefined
      }, () => {
        setSaveStatus('SUCCEEDED');
        onClose();
      }, () => setSaveStatus('FAILED')).catch(() => {});
    },
    saveFailed: saveStatus === 'FAILED',
    savePending: saveStatus === 'STARTED'
  };
  const shouldShowPastDateWarning = isScheduledForPastDate(stagedEnrollmentSchedule);
  return /*#__PURE__*/_jsxs(NavMarker, {
    name: "SCHEDULED_ENROLLMENT_PANEL",
    children: [/*#__PURE__*/_jsx(EnrollmentActionConfigHeader, {
      shouldHideTabs: isObjectlessWorkflow,
      header: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation-ui-enrollment.enrollmentHeader"
      }),
      enrollmentPath: revisionId && !isNaN(parseInt(revisionId, 10)) ? generatePath(Routes.FLOW_CANVAS_REVISION_ENROLLMENT_CONFIG_SCHEDULED, {
        screen: 'edit',
        flowId: flow.flowId,
        revisionId
      }) : generatePath(Routes.FLOW_CANVAS_SCHEDULED_ENROLLMENT, {
        screen: 'edit',
        flowId: flow.flowId
      }),
      unenrollmentPath: revisionId && !isNaN(parseInt(revisionId, 10)) ? generatePath(Routes.FLOW_CANVAS_REVISION_UNENROLLMENT_CONFIG_SCHEDULED, {
        screen: 'edit',
        flowId: flow.flowId,
        revisionId
      }) : generatePath(Routes.FLOW_CANVAS_SCHEDULED_UNENROLLMENT, {
        screen: 'edit',
        flowId: flow.flowId
      }),
      reenrollmentPath: revisionId && !isNaN(parseInt(revisionId, 10)) ? generatePath(Routes.FLOW_CANVAS_REVISION_REENROLLMENT_CONFIG_SCHEDULED, {
        screen: 'edit',
        flowId: flow.flowId,
        revisionId
      }) : generatePath(Routes.FLOW_CANVAS_SCHEDULED_REENROLLMENT, {
        screen: 'edit',
        flowId: flow.flowId
      }),
      children: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(UIButton, {
          onClick: onClose,
          responsive: false,
          use: "tertiary-light",
          size: "small",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation-ui-enrollment.redesign.cancelButton"
          })
        }), /*#__PURE__*/_jsx(EnrollmentSaveButton, Object.assign({
          use: "tertiary",
          size: "small"
        }, saveButtonCommonProps))]
      })
    }), /*#__PURE__*/_jsxs(Switch, {
      children: [/*#__PURE__*/_jsx(Route, {
        exact: true,
        path: [Routes.FLOW_CANVAS_SCHEDULED_UNENROLLMENT, Routes.FLOW_CANVAS_REVISION_UNENROLLMENT_CONFIG_SCHEDULED],
        children: /*#__PURE__*/_jsx(UIPanelBody, {
          children: /*#__PURE__*/_jsx(UIPanelSection, {
            children: unenrollmentComponent
          })
        })
      }), /*#__PURE__*/_jsx(Route, {
        exact: true,
        path: [Routes.FLOW_CANVAS_SCHEDULED_REENROLLMENT, Routes.FLOW_CANVAS_REVISION_REENROLLMENT_CONFIG_SCHEDULED],
        children: /*#__PURE__*/_jsx(UIPanelBody, {
          children: /*#__PURE__*/_jsx(UIPanelSection, {
            children: /*#__PURE__*/_jsx(ScheduledEnrollmentReenrollmentTab, {
              flowObjectTypeId: flow.objectTypeId
            })
          })
        })
      }), /*#__PURE__*/_jsx(Route, {
        exact: true,
        path: [Routes.FLOW_CANVAS_SCHEDULED_ENROLLMENT, Routes.FLOW_CANVAS_REVISION_ENROLLMENT_CONFIG_SCHEDULED, Routes.PLATFORM_FLOW_REVIEW_ENROLLMENT],
        children: /*#__PURE__*/_jsxs("div", {
          className: "flex-grow-1 display-block overflow-y-auto",
          style: {
            background: settingsComponent ? trellisTheme.color['do-not-use-gypsum'] : trellisTheme.color['do-not-use-olaf']
          },
          children: [/*#__PURE__*/_jsxs(UIPanelSection, {
            className: "m-bottom-0 p-bottom-8 p-top-6",
            style: {
              background: trellisTheme.color['do-not-use-olaf']
            },
            children: [!isObjectlessWorkflow && /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx(UIAlert, {
                className: "m-bottom-3",
                children: /*#__PURE__*/_jsx(FormattedMessageWithObjectTypeName, {
                  baseLangKey: "automation-ui-enrollment.redesign.enrollSelection.manualEnrollAlert_cob",
                  objectTypeId: flow.objectTypeId
                })
              }), /*#__PURE__*/_jsx(TriggerTypeChangeButton, {
                triggerType: "SCHEDULE",
                flowId: flow.flowId,
                resetFilters: clearEnrollmentListAndSchedule,
                hasExistingFilters: !!flow.enrollmentSchedule || hasScheduleChanged || !!initialEnrollmentFilters,
                readOnly: readOnly
              })]
            }), shouldShowPastDateWarning && /*#__PURE__*/_jsx(UIAlert, {
              className: "m-bottom-4",
              titleText: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "automation-ui-enrollment.redesign.scheduledTriggers.tabs.enrollment.pastDateWarning.title"
              }),
              type: "warning",
              children: /*#__PURE__*/_jsx(FormattedMessageWithObjectTypeName, {
                baseLangKey: "automation-ui-enrollment.redesign.scheduledTriggers.tabs.enrollment.pastDateWarning.description_cob",
                objectTypeId: flow.objectTypeId
              })
            }), /*#__PURE__*/_jsx(ScheduledEnrollmentTriggersTab, {
              flow: flow,
              isDataset: isDataset,
              readOnly: readOnly,
              stagedFilters: stagedFilters,
              setStagedFilters: setStagedFilters,
              stagedEnrollmentCriteriaForDataset: stagedEnrollmentCriteriaForDataset,
              setStagedEnrollmentCriteriaForDataset: setStagedEnrollmentCriteriaForDataset,
              flowObjectTypeId: flow.objectTypeId,
              stagedEnrollmentSchedule: stagedEnrollmentSchedule,
              setStagedEnrollmentSchedule: setStagedEnrollmentSchedule
            })]
          }), settingsComponent]
        })
      }), /*#__PURE__*/_jsx(Redirect, {
        path: "*",
        to: {
          pathname: `${currentUrl}/enrollment`,
          search: history.location.search
        }
      })]
    })]
  });
};
export default ScheduledEnrollmentContainer;